// VfxDirector contract (§24.2, §11). WP7 owns the implementation: every visual juice row of §11 that
// is not map-owned, triggered from the sim events (`onSim`) and from presented state (`update`).
//   - Instanced particle pools, ground decals and shockwaves (Particles.ts, Decals.ts, effects.ts).
//   - Comic text pops from a CanvasTexture atlas in Lilita One (TextPops.ts).
//   - Item and pebble telegraphs, teeter / ledge glows (Telegraphs.ts) and the push guide.
//   - Hitstop (§2.13): presented-pose freezes and catch-up (Hitstop.ts), which also masks the local
//     bean's reconciliation snap when it is hit online.
//   - Presentation slow motion for the round's final KO (0.5 s at 0.3×) and GAME! (0.6 s at 0.25×).
//   - Camera trauma and punches, chromatic pulses, the bomb's screen-space shockwave.
// Reduced Motion removes slow-motion flourishes (the camera removes shake and punches); Reduced
// Flashing removes white flashes and chromatic pulses and softens strobes and glows (§14.4).
import { Color, Group, Vector3, type Object3D, type PerspectiveCamera } from 'three';
import { MAX_BEANS, NO_SLOT, TUNE } from '@shared/constants';
import { EV, KoFlag, type SimEvent } from '@shared/events';
import { BS, Cause, EK, isHazardCause, ItemKind, ModeId, RoomPhase, RoundPhase } from '@shared/ids';
import { MAPS } from '@shared/maps/index';
import type { MapDef } from '@shared/maps/mapTypes';
import type { Vec2 } from '@shared/types';
import { createFrameView, type BeanView, type EntityView, type FrameView } from '../../game/FrameView';
import type { BeanRig } from '../bean/BeanRig';
import type { CameraDirector, CameraMoment } from '../camera/CameraDirector';
import type { MapPalette } from '../maps/MapView';
import type { MaterialLibrary } from '../materials/library';
import type { QualityTier } from '../Renderer';
import { TIERS } from '../quality';
import { Cell, spriteAtlas } from './atlas';
import { DecalSystem } from './Decals';
import { C, Fx, identityColor, identityHex } from './effects';
import { Hitstop, HITSTOP } from './Hitstop';
import { ADDITIVE_BOOST, Curve, ParticleSystem } from './Particles';
import { PushGuide } from './PushGuide';
import { RigFx } from './RigFx';
import { Telegraphs, type GroundFn } from './Telegraphs';
import { ITEM_ICON, PopPriority, TextPops, Word, type PopAnchor } from './TextPops';

export interface VfxDirector {
  readonly root: Object3D;
  onSim(ev: SimEvent, local: boolean, view: FrameView): void;  // spawns particles/pops/decals; triggers hitstop + camera
  update(view: FrameView): void;
  hitstopMs(slot: number): number;     // queried by Renderer for BeanRig.update
  setPalette(p: MapPalette): void;
}

/** Player settings the VFX honor (§14.4). */
export interface VfxSettings {
  reducedMotion: boolean;
  reducedFlashing: boolean;
  colorblind: boolean;
  /** The push guide is shown (Auto resolved against the Bean Level). */
  pushGuide: boolean;
}

/** The post-processing hooks VFX drive (WP7's PostChain implements them). */
export interface VfxPost {
  pulseChromatic(amount: number, ms: number): void;
  /** Screen-space refraction ring at a sim point (High only; a no-op elsewhere). */
  shockwave?(x: number, y: number, z: number): void;
  /** Sudden Death red vignette pulse. */
  redPulse?(): void;
  /** Soft full-screen flash; never called under Reduced Flashing. */
  flash?(color: Color, amount: number, ms: number): void;
}

export interface VfxDeps {
  rigs: readonly BeanRig[];
  camera: CameraDirector;
  post?: VfxPost | null;
  lib?: MaterialLibrary | null;
  tier?: QualityTier;
  settings?: () => VfxSettings;
}

/** WP7's director: the §24.2 contract plus what the Renderer and the verification page use. */
export interface GameVfx extends VfxDirector {
  readonly pops: TextPops;
  readonly particles: ParticleSystem;
  readonly decals: DecalSystem;
  readonly telegraphs: Telegraphs;
  readonly guide: PushGuide;
  readonly log: FxLog;
  /** The camera moment (final KO / GAME!) to frame, or null. */
  readonly moment: CameraMoment | null;
  /** Presentation time scale (1, or the slow-motion factor). */
  readonly timeScale: number;
  /** MATCH POINT letterbox bars are wanted. */
  readonly letterbox: boolean;
  /** The round winner once known (GAME! keeps color around it), or NO_SLOT. */
  readonly winner: number;
  /** Builds the presented view: hitstop-held bean poses and slow-motion dt. Call first each frame. */
  present(view: FrameView): FrameView;
  /** Lays out pops and applies per-render rig overrides; call after the camera update. */
  beforeRender(camera: PerspectiveCamera, view: FrameView): void;
  /** Restores per-render overrides; call after post.render. */
  afterRender(): void;
  setTier(t: QualityTier): void;
  setGround(fn: GroundFn): void;
  /** Void look for splashes (§12.2): the map's void kind and palette. */
  setVoid(kind: MapDef['voidKind'], p: MapPalette): void;
  /** New round: drops every effect, freeze and moment. */
  reset(): void;
}

/** The darkening tint for dust on pale floors (a warm plum grey). */
const DUST_SHADE = new Color('#8C7480');

// ---- Tuning (presentation-only, §17.2 boundary) ------------------------------------------------

export const JUICE = {
  /** §11.1 trauma: local bean involved / otherwise; heavy hit (Dizzy ≥ 85). */
  hitTraumaLocal: 0.25, hitTraumaOther: 0.1, heavyTrauma: 0.4,
  clashTrauma: 0.3, throwTrauma: 0.15, stompTrauma: 0.2, bigLandTrauma: 0.08,
  boomTrauma: 0.6, boomTraumaRadius: 6,
  koTraumaLocal: 0.3, koTraumaOther: 0.12,
  heavyChroma: 0.006, heavyChromaMs: 150,
  /** Zoom punches: heavy hit / KO 6% for 0.2 s, CLUTCH 4%. */
  heavyPunch: 0.06, koPunch: 0.06, punchMs: 200, clutchPunch: 0.04,
  /** Not in §11: a 2.5% kick for 120 ms on ordinary hits involving the local bean. */
  localHitPunch: 0.025, localHitPunchMs: 120,
  /** BONK! pop: 1.0× at 8 m/s, 1.6× at ≥ 15 m/s. */
  popKbLo: 8, popKbHi: 15, popScaleHi: 1.6,
  /** Final KO of a round: 0.5 s at 0.3×; GAME!: 0.6 s at 0.25×; both ease back over `slowOutSec`. */
  finalSlow: { scale: 0.3, sec: 0.5 }, gameSlow: { scale: 0.25, sec: 0.6 }, slowOutSec: 0.18,
  gameStampSec: 2.2,
  confettiPieces: 120,
  trailRate: 34, speedLineRate: 46, speedLineMin: 10,
  skidStep: 0.22, bigStep: 0.9,
  /** Item icon pop (§11.3: 0.8 s). */
  iconSec: 0.8,
  /** Seconds the camera keeps a KO splash in frame. */
  koHoldSec: 1.1,
  /** At most one HONK! word per bean per this many seconds (the ring shows on every honk). */
  honkPopSec: 1.5,
} as const;

// ---- Verification log ----------------------------------------------------------------------------

const LOG_CAP = 1024;

/** Which effect each event produced (the `?debug=vfx` page and unit tests read it). */
export class FxLog {
  private readonly evs = new Int32Array(LOG_CAP);
  private readonly tags: string[] = new Array<string>(LOG_CAP).fill('');
  private head = 0;
  private size = 0;
  /** Entries ever logged (monotonic; `since` reads past the ring's wrap with it). */
  total = 0;
  /** The event being handled (0 for state-driven effects). */
  current = 0;

  add(tag: string, ev: number = this.current): void {
    this.evs[this.head] = ev;
    this.tags[this.head] = tag;
    this.head = (this.head + 1) % LOG_CAP;
    this.size = Math.min(LOG_CAP, this.size + 1);
    this.total++;
  }

  /** Tags logged after the first `n` ever logged (at most the ring's capacity). Allocates: verification only. */
  since(n: number): string[] {
    const k = Math.min(this.size, Math.max(0, this.total - n));
    const out: string[] = [];
    for (let i = this.size - k; i < this.size; i++) out.push(this.tags[(this.head - this.size + i + LOG_CAP) % LOG_CAP]);
    return out;
  }

  /** Tags logged for `ev` (oldest first). Allocates: verification only. */
  tagsFor(ev: number): string[] {
    const out: string[] = [];
    for (let i = 0; i < this.size; i++) {
      const k = (this.head - this.size + i + LOG_CAP) % LOG_CAP;
      if (this.evs[k] === ev) out.push(this.tags[k]);
    }
    return out;
  }

  /** Every logged entry (oldest first). Allocates: verification only. */
  all(): { ev: number; tag: string }[] {
    const out: { ev: number; tag: string }[] = [];
    for (let i = 0; i < this.size; i++) {
      const k = (this.head - this.size + i + LOG_CAP) % LOG_CAP;
      out.push({ ev: this.evs[k], tag: this.tags[k] });
    }
    return out;
  }

  clear(): void {
    this.head = 0;
    this.size = 0;
  }
}

/** The pop tag logged for a word (`pop:BONK!`). */
export function popTag(w: Word): string {
  return `pop:${WORD_TEXT[w] ?? w}`;
}

const WORD_TEXT: Readonly<Partial<Record<Word, string>>> = {
  [Word.BONK]: 'BONK!', [Word.CLANG]: 'CLANG!', [Word.YOINK]: 'YOINK!', [Word.YEET]: 'YEET!', [Word.BREAK]: 'BREAK!',
  [Word.CLUTCH]: 'CLUTCH!', [Word.CRUNCH]: 'CRUNCH!', [Word.OOPS]: 'OOPS!', [Word.WHOOPS]: 'WHOOPS!', [Word.KABOOM]: 'KABOOM!',
  [Word.POW]: 'POW!', [Word.THWOP]: 'THWOP!', [Word.DOUBLE_BONK]: 'DOUBLE BONK!', [Word.PLINK]: 'PLINK!', [Word.HONK]: 'HONK!',
  [Word.CLUTCH_BALLOON]: 'CLUTCH!(balloon)', [Word.GAME]: 'GAME!', [Word.BIG]: 'BIG!', [Word.POP]: 'POP!', [Word.HOT]: 'HOT!',
};

// ---- Helpers -------------------------------------------------------------------------------------

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function rand(a: number, b: number): number {
  return a + Math.random() * (b - a);
}

/** Copies every FrameView field by reference except `beans` (the presented copies). */
function shareFrame(dst: FrameView, src: FrameView, beans: BeanView[]): void {
  dst.now = src.now; dst.dt = src.dt; dst.timeScale = src.timeScale; dst.source = src.source;
  dst.roomPhase = src.roomPhase; dst.mapId = src.mapId; dst.modeId = src.modeId; dst.golden = src.golden; dst.mutator = src.mutator;
  dst.roundIndex = src.roundIndex; dst.totalRounds = src.totalRounds; dst.roundId = src.roundId;
  dst.renderTick = src.renderTick; dst.localTick = src.localTick; dst.roundTick = src.roundTick;
  dst.clock = src.clock; dst.localSlot = src.localSlot; dst.focusSlot = src.focusSlot;
  dst.beans = beans; dst.ents = src.ents; dst.map = src.map; dst.hazard = src.hazard; dst.mode = src.mode; dst.net = src.net;
  dst.replay = src.replay; dst.excitement = src.excitement; dst.grade = src.grade;
}

function inPlay(b: BeanView | undefined): b is BeanView {
  return b !== undefined && b.active && !b.out && !b.ghost;
}

function isScoringMode(m: ModeId): boolean {
  return m !== ModeId.LOBBY && m !== ModeId.PODIUM;
}

/** States in which the trail cosmetic shows (§7.3: lunge, flight and fall). */
function trailState(b: BeanView, speed: number): boolean {
  return b.state === BS.LUNGE || b.state === BS.FALLING || b.state === BS.YANKED || (b.state === BS.TUMBLE && speed > 5);
}

interface Slow { scale: number; leftSec: number }

const EMOTE_CELL: Readonly<Record<number, Cell>> = { 1: Cell.SPARKLE, 2: Cell.NOTE, 3: Cell.HEART, 4: Cell.STAR };

export function createVfxDirector(deps: VfxDeps): GameVfx {
  const root = new Group();
  root.name = 'vfx';
  const atlas = spriteAtlas();
  let tier: QualityTier = deps.tier ?? 'high';
  const particles = new ParticleSystem(atlas, TIERS[tier].particles);
  const decals = new DecalSystem(atlas, 192);
  const fx = new Fx(particles, decals);
  const pops = new TextPops();
  const hitstop = new Hitstop();
  const rigFx = deps.lib ? new RigFx(deps.lib) : null;
  const log = new FxLog();
  root.add(particles.root, decals.mesh);

  let ground: GroundFn = () => 0;
  const groundAt: GroundFn = (x, y) => ground(x, y);
  const telegraphs = new Telegraphs(decals, groundAt);
  const guide = new PushGuide(decals, groundAt);

  const presented: BeanView[] = createFrameView().beans;
  const pv: FrameView = createFrameView();
  const dir: Vec2 = { x: 0, y: 0 };
  // Per-slot state-driven accumulators.
  const trailAcc = new Float32Array(MAX_BEANS);
  const lineAcc = new Float32Array(MAX_BEANS);
  const skidAcc = new Float32Array(MAX_BEANS);
  const stepAcc = new Float32Array(MAX_BEANS);
  const lastX = new Float32Array(MAX_BEANS);
  const lastY = new Float32Array(MAX_BEANS);
  const seen = new Uint8Array(MAX_BEANS);
  /** Presentation time of each bean's last HONK! word. */
  const honkAt = new Float64Array(MAX_BEANS).fill(-Infinity);
  /** Sweat drips from the hands while a bean's ledge cooldown runs. */
  const dripAcc = new Float32Array(MAX_BEANS);
  const headTmp = new Vector3();
  const crateZ = new Map<number, number>();

  let lastNow = -1;
  let realDt = 0;
  let time = 0;
  let slow: Slow | null = null;
  let timeScale = 1;
  let moment: CameraMoment | null = null;
  let momentRound = -1;
  let gameShown = -1;
  let winner = NO_SLOT;
  let last: FrameView = pv;
  let letterbox = false;

  const settings = (): VfxSettings => deps.settings?.() ?? { reducedMotion: false, reducedFlashing: false, colorblind: false, pushGuide: false };

  const anchors: PopAnchor = {
    headOf(slot, out) {
      const b = presented[slot];
      const rig = deps.rigs[slot];
      if (!b || !b.active || !rig || !rig.root.visible) return false;
      rig.headWorldPos(out);
      return true;
    },
    plateOf(slot, out) {
      // Matches ui/hud/Nameplates: hidden for out beans, at the body top (1.1 m, Big Shake scaled) + 0.35 m.
      const b = presented[slot];
      const rig = deps.rigs[slot];
      if (!b || !b.active || b.out || !rig || !rig.root.visible) return false;
      // Replays show only the focus bean's plate (§12.4).
      if (last.replay && slot !== last.replay.focusSlot) return false;
      // The rig root carries the presented (lifted) feet position; Nameplates reads the same frame.
      const s = b.radius > 0 ? b.radius / TUNE.bean.radius : 1;
      const r = rig.root.position;
      out.set(r.x, r.y + 1.1 * s + 0.35, r.z);
      return true;
    },
  };

  // ---- Small building blocks ----------------------------------------------------------------

  function tag(t: string): void {
    log.add(t);
  }

  function groundUnder(x: number, y: number, fallback = 0): number {
    const g = ground(x, y);
    return Number.isFinite(g) ? g : fallback;
  }

  function pop(word: Word, x: number, y: number, z: number, scale = 1, slot = -1, priority: PopPriority = PopPriority.NORMAL): void {
    // GAME! owns the screen: no world pops compete with its stamp and the winner.
    if (last.grade === 'game' || moment?.kind === 'game') return;
    if (pops.spawn(word, x, y, z, scale, slot, undefined, 0, priority)) tag(popTag(word));
  }

  function freeze(slot: number, ms: number): void {
    if (slot === NO_SLOT || slot >= MAX_BEANS || ms <= 0) return;
    hitstop.freeze(slot, ms);
    tag('hitstop');
  }

  function flash(slot: number): void {
    if (slot === NO_SLOT || slot >= MAX_BEANS || settings().reducedFlashing) return;
    hitstop.flash(slot, HITSTOP.flashMs);
    tag('flash');
  }

  function trauma(a: number): void {
    deps.camera.addTrauma(a);
    tag('trauma');
  }

  function punch(amount: number, ms: number): void {
    deps.camera.punch(amount, ms);
    tag('punch');
  }

  function chroma(amount: number, ms: number): void {
    if (settings().reducedFlashing) return;
    deps.post?.pulseChromatic(amount, ms);
    tag('chroma');
  }

  function involvesLocal(view: FrameView, a: number, b: number): boolean {
    return view.localSlot !== NO_SLOT && (a === view.localSlot || b === view.localSlot);
  }

  function beanAt(view: FrameView, slot: number): BeanView | undefined {
    if (slot === NO_SLOT || slot >= MAX_BEANS) return undefined;
    const b = view.beans[slot];
    return b && b.active ? b : undefined;
  }

  function identity(b: BeanView | undefined): Color {
    return b ? identityColor(b.identity, settings().colorblind) : C.yellow;
  }

  function kick(slot: number, kind: Parameters<BeanRig['impulse']>[0], strength: number, dx?: number, dy?: number): void {
    const rig = slot !== NO_SLOT ? deps.rigs[slot] : undefined;
    if (!rig) return;
    if (dx !== undefined && dy !== undefined) {
      const l = Math.hypot(dx, dy) || 1;
      dir.x = dx / l;
      dir.y = dy / l;
      rig.impulse(kind, strength, dir);
    } else {
      rig.impulse(kind, strength);
    }
  }

  function startSlow(scale: number, sec: number): void {
    if (settings().reducedMotion) return;
    slow = { scale, leftSec: sec + JUICE.slowOutSec };
    tag('slowmo');
  }

  function celebrate(kind: number, x: number, y: number, h: number, main: Color): void {
    switch (kind) {
      case 1:
        fx.fireworks(x, y, h, main);
        tag('celebration:fireworks');
        break;
      case 2:
        fx.flowerBurst(x, y, h, main);
        tag('celebration:flowers');
        break;
      case 3:
        fx.disco(x, y, h, main);
        tag('celebration:disco');
        break;
      default:
        // A cannon: launched from the splash so it bursts above the rim and rains over the arena.
        fx.confetti(x, y, h, JUICE.confettiPieces, main, 15, 5, h);
        tag('celebration:confetti');
        break;
    }
    tag('confetti');
  }

  // ---- Event recipes (§11) --------------------------------------------------------------------

  function onHit(ev: SimEvent, view: FrameView): void {
    const kb = ev.d / 10;
    const victim = beanAt(view, ev.b);
    const attacker = beanAt(view, ev.a);
    const cause = ev.c as Cause;
    const heavyDizzy = victim !== undefined && victim.dizzy >= HITSTOP.heavyDizzy;
    const heavy = cause === Cause.GLOVE || cause === Cause.BOMB || heavyDizzy;
    const ms = heavy ? HITSTOP.heavyMs : HITSTOP.normalMs;
    freeze(ev.a, ms);
    freeze(ev.b, ms);
    flash(ev.a);
    flash(ev.b);
    // Knockback direction: attacker → victim (or contact → victim for hazards and blasts).
    let kx = 0;
    let ky = 0;
    if (victim && attacker) {
      kx = victim.x - attacker.x;
      ky = victim.y - attacker.y;
    } else if (victim) {
      kx = victim.x - ev.x;
      ky = victim.y - ev.y;
    }
    const kl = Math.hypot(kx, ky);
    if (kl > 1e-3) {
      kx /= kl;
      ky /= kl;
    }
    kick(ev.b, 'hit', kb * 0.25, kx, ky);
    if (attacker) kick(ev.a, 'squash', 0.8 + kb * 0.05, -kx, -ky);

    const strength = Math.min(2.1, Math.max(0.75, kb / 8));
    const cz = (victim ? Math.max(victim.z, 0) : 0) + 0.65;
    const hx = victim && attacker ? (victim.x + attacker.x) / 2 : ev.x;
    const hy = victim && attacker ? (victim.y + attacker.y) / 2 : ev.y;
    const hazard = isHazardCause(cause);
    if (cause !== Cause.BOMB) {
      fx.impact(hx, hy, cz, kx, ky, strength * (hazard ? 0.75 : 1), heavy ? 16 : 12, settings().reducedFlashing);
      tag('impact');
      if (victim) {
        const h = groundUnder(victim.x, victim.y, victim.z);
        if (victim.z - h < 0.3) {
          fx.dustPuffs(victim.x, victim.y, h, 4, -kx, -ky, 2.2, 0.36);
          if (strength > 1.15 || heavy) {
            fx.groundWave(victim.x, victim.y, h, 1.2 + 0.6 * strength, heavyDizzy ? C.danger : C.white, 0.24, 0.85);
            tag('shockwave');
          }
        }
      }
    }
    // Pops about the local bean (or a huge hit) always show and read bigger; the rest is chatter that
    // yields when the screen is busy. In replays the subject is the focus bean and its killer.
    const local = involvesLocal(view, ev.a, ev.b);
    const rp = view.replay;
    const subject = rp ? ev.b === rp.focusSlot || ev.a === rp.focusSlot : local;
    const prio = subject ? (rp ? PopPriority.NORMAL : PopPriority.HIGH) : rp ? PopPriority.LOW : heavy || kb >= JUICE.popKbHi ? PopPriority.NORMAL : PopPriority.LOW;
    const popScale = (1 + (JUICE.popScaleHi - 1) * clamp01((kb - JUICE.popKbLo) / (JUICE.popKbHi - JUICE.popKbLo))) * (subject ? 1.1 : 0.78);
    if (cause === Cause.GLOVE) pop(Word.POW, hx, hy, cz + 0.8, 1.25 * popScale, -1, Math.max(prio, PopPriority.NORMAL) as PopPriority);
    else if (cause === Cause.SHOVE || cause === Cause.THROW || cause === Cause.BODY || cause === Cause.CLASH) pop(Word.BONK, hx, hy, cz + 0.8, popScale, -1, prio);
    else if (cause === Cause.PLUNGER) pop(Word.BONK, hx, hy, cz + 0.8, 0.85, -1, prio);

    if (heavyDizzy) {
      trauma(JUICE.heavyTrauma);
      chroma(JUICE.heavyChroma, JUICE.heavyChromaMs);
      punch(JUICE.heavyPunch, JUICE.punchMs);
    } else {
      trauma(local ? JUICE.hitTraumaLocal : JUICE.hitTraumaOther);
      // A small zoom kick on hits you deal or take: the §11 trauma alone moves the view ~2 cm.
      if (local && view.source !== 'replay') punch(JUICE.localHitPunch, JUICE.localHitPunchMs);
    }
  }

  function onClash(ev: SimEvent, view: FrameView): void {
    freeze(ev.a, HITSTOP.clashMs);
    freeze(ev.b, HITSTOP.clashMs);
    flash(ev.a);
    flash(ev.b);
    const a = beanAt(view, ev.a);
    const b = beanAt(view, ev.b);
    const h = groundUnder(ev.x, ev.y);
    if (a && b) {
      kick(ev.a, 'hit', 2.2, a.x - b.x, a.y - b.y);
      kick(ev.b, 'hit', 2.2, b.x - a.x, b.y - a.y);
    }
    fx.sparkFountain(ev.x, ev.y, h + 0.75, h, 30);
    tag('sparks');
    fx.airRing(ev.x, ev.y, h + 0.75, 3, C.spark, 0.22);
    pop(Word.CLANG, ev.x, ev.y, h + 1.7, 1.1);
    trauma(JUICE.clashTrauma);
  }

  function onKo(ev: SimEvent, view: FrameView, replay: boolean): void {
    const flags = ev.d;
    const credit = beanAt(view, ev.b);
    const victim = view.beans[ev.a];
    const voidZ = TUNE.edge.killZ;
    fx.splash(ev.x, ev.y, voidZ, 24);
    tag('splash');
    if (credit && (flags & KoFlag.SELF) === 0 && ev.b !== ev.a) {
      celebrate(credit.cosmetics.celebration, ev.x, ev.y, voidZ, identity(credit));
    } else {
      fx.confetti(ev.x, ev.y, voidZ, 40, victim ? identity(victim) : C.white, 9, 3, voidZ);
      tag('confetti');
    }
    // Pops float above the rim so they read from the gameplay camera.
    const pz = Math.max(1.6, groundUnder(ev.x, ev.y, 0) + 1.6);
    if (flags & KoFlag.SELF) pop(Word.OOPS, ev.x, ev.y, pz, 1.15);
    if (flags & KoFlag.DOUBLE) pop(Word.DOUBLE_BONK, ev.x, ev.y, pz + 0.6, 1.15);
    if ((flags & KoFlag.BOUNTY) && victim) {
      fx.crownFly(ev.x, ev.y, voidZ + 1);
      tag('crown');
    }
    punch(JUICE.koPunch, JUICE.punchMs);
    trauma(involvesLocal(view, ev.a, ev.b) ? JUICE.koTraumaLocal : JUICE.koTraumaOther);
    // Keep the splash on screen for its whole show (the camera otherwise frames the living only).
    const cam = deps.camera as CameraDirector & { holdInFrame?(x: number, y: number, z: number, sec: number): void };
    // Both the column's top and its base on the surface are held, so the splash never sits on the
    // screen edge under the HUD.
    if (!replay) {
      cam.holdInFrame?.(ev.x, ev.y, voidZ + 3.2, JUICE.koHoldSec);
      cam.holdInFrame?.(ev.x, ev.y, voidZ + 0.2, JUICE.koHoldSec);
    }
    if (replay || (flags & KoFlag.FINAL) === 0 || !isScoringMode(view.modeId)) return;
    // The round's final KO: slow motion and an 85% framing on the victim (§11.2).
    const matchFinal = view.grade === 'game' || (view.totalRounds > 0 && view.roundIndex === view.totalRounds - 1);
    winner = findWinner(view, ev.a);
    if (matchFinal) {
      startGame(view, ev.x, ev.y, ev.a);
    } else {
      startSlow(JUICE.finalSlow.scale, JUICE.finalSlow.sec);
      setMoment('finalKo', ev.x, ev.y, ev.a, view);
    }
  }

  function findWinner(view: FrameView, victim: number): number {
    let w = NO_SLOT;
    for (const b of view.beans) {
      if (!inPlay(b) || b.slot === victim || b.state === BS.FALLING) continue;
      if (w !== NO_SLOT) return NO_SLOT;
      w = b.slot;
    }
    return w;
  }

  function setMoment(kind: CameraMoment['kind'], x: number, y: number, victim: number, view: FrameView): void {
    moment = { kind, x, y, victim, winner };
    momentRound = view.roundId;
  }

  /** GAME! (§11.2): 0.6 s at 0.25×, the desaturating grade (post reads view.grade), the stamp. */
  function startGame(view: FrameView, x: number, y: number, victim: number): void {
    if (gameShown === view.roundId) return;
    gameShown = view.roundId;
    startSlow(JUICE.gameSlow.scale, JUICE.gameSlow.sec);
    setMoment('game', x, y, victim, view);
    pops.showStamp(Word.GAME, JUICE.gameStampSec);
    tag('game');
    tag(popTag(Word.GAME));
    trauma(0.35);
  }

  function itemUse(ev: SimEvent, view: FrameView): void {
    const b = beanAt(view, ev.a);
    if (!b) return;
    const h = groundUnder(b.x, b.y, b.z);
    switch (ev.b) {
      case ItemKind.BANANA: {
        const bx = b.x - Math.cos(b.facing) * TUNE.banana.placeBehind;
        const by = b.y - Math.sin(b.facing) * TUNE.banana.placeBehind;
        fx.splat(bx, by, groundUnder(bx, by, h), C.banana, 0.85, 1.6);
        tag('splat');
        break;
      }
      case ItemKind.BOMB:
        fx.swish(b.x, b.y, b.z + 0.4, b.facing, C.white, 1.2);
        tag('swish');
        break;
      case ItemKind.PLUNGER:
        fx.swish(b.x, b.y, b.z + 0.2, b.facing, C.white, 0.9);
        tag('swish');
        break;
      default:
        fx.sparkles(b.x, b.y, b.z + 0.8, 6, C.white, 0.8, 1.5);
        tag('sparkles');
        break;
    }
  }

  function iconPop(slot: number, kind: number, view: FrameView): void {
    const b = beanAt(view, slot);
    const word = ITEM_ICON[kind];
    if (!b || word === undefined) return;
    pops.spawn(word, b.x, b.y, b.z + 1.9, 1, slot, 0.35, JUICE.iconSec);
    tag('icon');
  }

  function onBoom(x: number, y: number, radius: number, view: FrameView, potato: boolean): void {
    const h = groundUnder(x, y, 0);
    const reduced = settings().reducedFlashing;
    fx.explosion(x, y, h + 0.2, h, radius, potato ? C.fireMid : C.smoke, reduced);
    tag('explosion');
    tag('shockwave');
    tag('scorch');
    pop(Word.KABOOM, x, y, h + 2.1, potato ? 1.5 : 1.35);
    deps.post?.shockwave?.(x, y, h + 0.5);
    // §11.3: trauma +0.6 within 6 m (of the local bean; spectators always feel it).
    const me = beanAt(view, view.localSlot);
    const d = me && !me.ghost ? Math.hypot(me.x - x, me.y - y) : 0;
    trauma(JUICE.boomTrauma * (d <= JUICE.boomTraumaRadius ? 1 : Math.max(0.25, 1 - (d - JUICE.boomTraumaRadius) / 8)));
    punch(0.05, 220);
    if (!reduced && deps.post?.flash) {
      deps.post.flash(C.fireCore, 0.22, 90);
      tag('screenFlash');
    }
  }

  function handle(ev: SimEvent, view: FrameView, replay: boolean): void {
    const a = beanAt(view, ev.a);
    switch (ev.type) {
      case EV.LUNGE: {
        if (!a) return;
        kick(ev.a, 'lunge', 1.5, Math.cos(a.facing), Math.sin(a.facing));
        const h = groundUnder(a.x, a.y, a.z);
        fx.dustPuffs(a.x, a.y, h, 3, Math.cos(a.facing), Math.sin(a.facing), 1.8, 0.55);
        tag('dust');
        trailBurst(a, 6);
        return;
      }
      case EV.HIT: return onHit(ev, view);
      case EV.CLASH: return onClash(ev, view);
      case EV.GRAB: {
        const v = beanAt(view, ev.b);
        kick(ev.a, 'squash', 1.6);
        const x = v ? v.x : a?.x ?? ev.x;
        const y = v ? v.y : a?.y ?? ev.y;
        pop(Word.YOINK, x, y, (v?.z ?? 0) + 1.7, 0.8);
        fx.sparkles(x, y, (v?.z ?? 0) + 0.9, 5, C.pink, 0.7, 1.2);
        return;
      }
      case EV.GRAB_MISS:
        if (!a) return;
        fx.swish(a.x, a.y, a.z + 0.2, a.facing, C.white, 1.0);
        tag('swish');
        fx.dustPuffs(a.x, a.y, groundUnder(a.x, a.y, a.z), 2, Math.cos(a.facing), Math.sin(a.facing), 1, 0.3);
        return;
      case EV.THROW: {
        const v = beanAt(view, ev.b);
        const x = v ? v.x : a?.x ?? ev.x;
        const y = v ? v.y : a?.y ?? ev.y;
        pop(Word.YEET, x, y, (v?.z ?? 0) + 1.8, 1.15);
        if (a) fx.swish(a.x, a.y, a.z + 0.5, a.facing, C.white, 1.4);
        const f = a?.facing ?? 0;
        if (v) for (let i = 0; i < 8; i++) fx.speedLine(v.x, v.y, v.z, v.vx || Math.cos(f) * 10, v.vy || Math.sin(f) * 10, v.vz);
        tag('speedlines');
        trauma(JUICE.throwTrauma);
        return;
      }
      case EV.ESCAPE: {
        const c = beanAt(view, ev.b);
        if (a && c) {
          kick(ev.a, 'hit', 1.6, a.x - c.x, a.y - c.y);
          kick(ev.b, 'hit', 1.6, c.x - a.x, c.y - a.y);
        }
        const x = a ? a.x : ev.x;
        const y = a ? a.y : ev.y;
        pop(Word.BREAK, x, y, (a?.z ?? 0) + 1.8, 1);
        fx.impact(x, y, (a?.z ?? 0) + 0.9, 0, 0, 0.7, 6, settings().reducedFlashing);
        tag('impact');
        return;
      }
      case EV.JUMP:
        if (!a) return;
        kick(ev.a, 'lunge', 1.0);
        fx.dustPuffs(a.x, a.y, groundUnder(a.x, a.y, a.z), 2, 0, 0, 1.0, 0.3);
        tag('dust');
        return;
      case EV.LAND: {
        const speed = ev.d / 10;
        kick(ev.a, 'land', speed * 0.12);
        const big = a?.big ?? false;
        const r = Math.min(1.6, 0.35 + speed * 0.09) * (big ? 1.6 : 1);
        if (speed > 2 || big) {
          fx.dustRing(ev.x, ev.y, groundUnder(ev.x, ev.y, a?.z ?? 0), r, big ? 12 : 8);
          tag('dustRing');
        }
        if (big) trauma(JUICE.bigLandTrauma);
        return;
      }
      case EV.TEETER:
        if (!a) return;
        fx.dustPuffs(ev.x, ev.y, groundUnder(a.x, a.y, a.z), 2, a.edgeNx, a.edgeNy, 0.8, 0.3);
        tag('teeterGlow');
        return;
      case EV.TEETER_SAVE:
        if (!a || ev.c !== 1) return;
        pop(Word.CLUTCH, a.x, a.y, a.z + 1.9, 1.2, ev.a);
        fx.sparkles(a.x, a.y, a.z + 0.6, 16, C.gold, 1.4, 2.6);
        tag('sparkles');
        punch(JUICE.clutchPunch, JUICE.punchMs);
        return;
      case EV.LEDGE:
        fx.dustPuffs(ev.x, ev.y, groundUnder(ev.x, ev.y), 3, 0, 0, 1.2, 0.3);
        tag('stompRing');
        return;
      case EV.CLIMB:
        if (!a) return;
        fx.dustPuffs(a.edgeX, a.edgeY, groundUnder(a.edgeX, a.edgeY), 3, -a.edgeNx, -a.edgeNy, 1.2, 0.32);
        tag('dust');
        return;
      case EV.STOMP: {
        const v = beanAt(view, ev.b);
        const onRim = v !== undefined && (v.edgeX !== 0 || v.edgeY !== 0);
        const x = onRim ? v.edgeX : v?.x ?? a?.x ?? ev.x;
        const y = onRim ? v.edgeY : v?.y ?? a?.y ?? ev.y;
        const h = groundUnder(x, y);
        kick(ev.b, 'squash', 3);
        kick(ev.a, 'land', 1.2);
        fx.impact(x, y, h + 0.15, 0, 0, 1, 10, settings().reducedFlashing);
        tag('impact');
        fx.dustRing(x, y, h, 0.9, 8);
        fx.cracks(x, y, h, 1.3);
        pop(Word.CRUNCH, x, y, h + 1.5, 1.2);
        trauma(JUICE.stompTrauma);
        return;
      }
      case EV.FALL: {
        // The round's final KO hit freezes for 150 ms (§2.13): a hit that drops the last challenger.
        if (a && isScoringMode(view.modeId) && view.modeId !== ModeId.CROWN_GRAB && hitstop.ms(ev.a) > 0) {
          let alive = 0;
          for (const b of view.beans) if (inPlay(b) && b.slot !== ev.a && b.state !== BS.FALLING) alive++;
          if (alive <= 1) {
            const extra = HITSTOP.finalMs - HITSTOP.normalMs;
            freeze(ev.a, hitstop.ms(ev.a) + extra);
            if (hitstop.ms(ev.b) > 0) freeze(ev.b, hitstop.ms(ev.b) + extra);
          }
        }
        if (a) {
          fx.swish(a.x, a.y, a.z + 0.4, Math.atan2(-a.edgeNy, -a.edgeNx), C.white, 1.1);
          tag('swish');
        }
        return;
      }
      case EV.KO: return onKo(ev, view, replay);
      case EV.BALLOON_SAVE:
        if (!a) return;
        pop(Word.CLUTCH_BALLOON, a.x, a.y, a.z + 2.2, 1.15, ev.a);
        fx.sparkles(a.x, a.y, a.z + 0.8, 14, C.pink, 1.2, 2.4);
        tag('sparkles');
        punch(JUICE.clutchPunch, JUICE.punchMs);
        return;
      case EV.ITEM_GET:
        iconPop(ev.a, ev.b, view);
        if (a) fx.sparkles(a.x, a.y, a.z + 1.2, 8, C.gold, 0.9, 1.6);
        return;
      case EV.ITEM_USE: return itemUse(ev, view);
      case EV.CRATE_DROP:
        tag('crateShadow');
        return;
      case EV.CRATE_OPEN: {
        const e = entity(view, ev.c);
        const x = e ? e.x : a?.x ?? ev.x;
        const y = e ? e.y : a?.y ?? ev.y;
        const h = groundUnder(x, y);
        fx.crateBurst(x, y, h + Math.max(0, e?.z ?? 0), h, settings().reducedFlashing);
        tag('flaps');
        tag('sparkles');
        iconPop(ev.a, ev.b, view);
        return;
      }
      case EV.PEEL_SLIP:
        if (!a) return;
        pop(Word.WHOOPS, a.x, a.y, a.z + 1.8, 1.05, ev.a);
        fx.splat(a.x, a.y, groundUnder(a.x, a.y, a.z), C.banana, 1.1, 2);
        fx.skid(a.x, a.y, groundUnder(a.x, a.y, a.z), Math.atan2(a.vy, a.vx));
        tag('skid');
        kick(ev.a, 'squash', 1.4);
        return;
      case EV.BOMB_LAND: {
        const h = groundUnder(ev.x, ev.y);
        fx.dustPuffs(ev.x, ev.y, h, 4, 0, 0, 1.4, 0.32);
        tag('dust');
        tag('bombRing');
        return;
      }
      case EV.BOMB_BOOM: return onBoom(ev.x, ev.y, TUNE.bomb.radius, view, false);
      case EV.PLUNGER_HIT: {
        const v = beanAt(view, ev.b);
        if (!v) return;
        pop(Word.THWOP, v.x, v.y, v.z + 1.7, 1.05);
        fx.impact(v.x, v.y, v.z + 0.7, 0, 0, 0.6, 5, settings().reducedFlashing);
        tag('impact');
        kick(ev.b, 'hit', 1.8);
        return;
      }
      case EV.SHAKE_START:
        if (!a) return;
        kick(ev.a, 'grow', 3);
        fx.dustRing(a.x, a.y, groundUnder(a.x, a.y, a.z), 1.2, 10);
        fx.sparkles(a.x, a.y, a.z + 1.2, 12, C.positive, 1.4, 2);
        pop(Word.BIG, a.x, a.y, a.z + 2.4, 1.2, ev.a);
        tag('dustRing');
        return;
      case EV.SHAKE_END:
        if (!a) return;
        kick(ev.a, 'squash', 1.5);
        fx.dustPuffs(a.x, a.y, groundUnder(a.x, a.y, a.z), 5, 0, 0, 1.6, 0.4);
        tag('dust');
        return;
      case EV.PUNCH_WINDUP:
        tag('punchArc');
        return;
      case EV.PUNCH:
        if (!a) return;
        fx.swish(a.x, a.y, a.z + 0.3, a.facing, C.danger, 1.5);
        tag('swish');
        return;
      case EV.FLOAT_START:
        if (!a) return;
        fx.sparkles(a.x, a.y, a.z + 1.6, 8, identity(a), 0.8, 1.6);
        tag('sparkles');
        return;
      case EV.FLOAT_POP:
        if (!a) return;
        fx.shreds(a.x, a.y, a.z + 1.9, identity(a), 12);
        tag('shreds');
        pop(Word.POP, a.x, a.y, a.z + 2.3, 0.9);
        return;
      case EV.PEBBLE_DROP:
        tag('pebbleTelegraph');
        return;
      case EV.PEBBLE_HIT: {
        const v = beanAt(view, ev.b);
        if (!v) return;
        fx.impact(v.x, v.y, v.z + 1.1, 0, 0, 0.55, 6, settings().reducedFlashing);
        tag('impact');
        pop(Word.PLINK, v.x, v.y, v.z + 2, 0.8);
        kick(ev.b, 'hit', 1.2);
        return;
      }
      case EV.HONK: {
        if (!a) return;
        // Honks are chatter: the ring always shows, the word at most once per 1.5 s per bean, and
        // only yours is normal priority (bots honk a lot; BONK!s must stay the loudest words).
        fx.airRing(a.x, a.y, a.z + 0.9, 1.6, identity(a), 0.2);
        if (time - honkAt[ev.a] < JUICE.honkPopSec) return;
        honkAt[ev.a] = time;
        const mine = ev.a === view.localSlot;
        pop(Word.HONK, a.x, a.y, a.z + 1.9, mine ? 0.75 : 0.6, ev.a, mine ? PopPriority.NORMAL : PopPriority.LOW);
        return;
      }
      case EV.EMOTE:
        if (!a) return;
        emote(a, ev.b);
        return;
      case EV.PHASE:
        if (ev.b === RoundPhase.SUDDEN_DEATH && !replay) {
          if (!settings().reducedFlashing) {
            deps.post?.redPulse?.();
            tag('redPulse');
          }
          trauma(0.2);
        }
        return;
      case EV.CROWN_POP:
        fx.sparkles(ev.x, ev.y, 1.4, 14, C.gold, 1.4, 3);
        tag('sparkles');
        fx.airRing(ev.x, ev.y, 1.2, 2.2, C.gold, 0.24);
        return;
      case EV.CROWN_RESPAWN: {
        // The lost crown drops back in from 4 m above its spawn point: a gold glint where it appears
        // and a gold ring on the floor where it will land, so everyone sees where to run.
        const h = groundUnder(ev.x, ev.y);
        fx.sparkles(ev.x, ev.y, h + 4, 12, C.gold, 1.1, 1.2);
        tag('sparkles');
        fx.groundWave(ev.x, ev.y, h, 1.6, C.gold, 0.5, 0.9);
        return;
      }
      case EV.CROWN_PICK:
        if (!a) return;
        fx.sparkles(a.x, a.y, a.z + 1.4, 14, C.gold, 1.2, 2.6);
        tag('sparkles');
        return;
      case EV.POTATO_NEW:
      case EV.POTATO_PASS: {
        const to = ev.type === EV.POTATO_NEW ? a : beanAt(view, ev.b);
        if (!to) return;
        pop(Word.HOT, to.x, to.y, to.z + 1.9, 1, to.slot);
        fx.sparkles(to.x, to.y, to.z + 1.1, 10, C.fireMid, 0.9, 1.8);
        tag('sparkles');
        return;
      }
      case EV.POTATO_BOOM: return onBoom(ev.x, ev.y, TUNE.potato.blastRadius, view, true);
      case EV.RESPAWN: {
        const h = groundUnder(ev.x, ev.y);
        fx.sparkles(ev.x, ev.y, h + 0.4, 10, a ? identity(a) : C.white, 1.2, 2);
        tag('sparkles');
        return;
      }
      case EV.MATCH_POINT:
        if (replay) return;
        letterbox = true;
        tag('letterbox');
        return;
      case EV.ROUND_OVER: {
        const w = beanAt(view, ev.a);
        if (!w) return;
        if (!replay && winner === NO_SLOT) winner = ev.a;
        // The round winner's party: a confetti fountain in its color, a gold sparkle burst and a gold
        // ring racing out across the floor.
        const h = groundUnder(w.x, w.y, w.z);
        fx.confetti(w.x, w.y, w.z + 1.5, 90, identity(w), 11, 4, h);
        fx.sparkles(w.x, w.y, w.z + 1, 16, C.gold, 1.6, 3);
        fx.groundWave(w.x, w.y, h, 3.2, C.gold, 0.45, 0.9);
        tag('confetti');
        return;
      }
      case EV.FLUNG:
        if (!a) return;
        for (let i = 0; i < 8; i++) fx.speedLine(a.x, a.y, a.z, a.vx, a.vy, a.vz);
        tag('speedlines');
        return;
      case EV.DUMMY_HIT: {
        const e = entity(view, ev.c);
        const x = e ? e.x : ev.x;
        const y = e ? e.y : ev.y;
        const h = groundUnder(x, y);
        fx.impact(x, y, h + 0.8, 0, 0, 0.8, 8, settings().reducedFlashing);
        tag('impact');
        pop(Word.BONK, x, y, h + 1.8, 0.8);
        return;
      }
      case EV.GHOST_READY:
        if (!a) return;
        fx.sparkles(a.x, a.y, a.z - 0.4, 5, C.silver, 0.6, 0.8);
        tag('sparkles');
        return;
      case EV.DETHRONE: {
        const c = beanAt(view, ev.b);
        if (!c) return;
        fx.confetti(c.x, c.y, c.z + 1.5, 50, identity(c), 8, 3, groundUnder(c.x, c.y, c.z));
        tag('confetti');
        return;
      }
      case EV.READY_PAD:
        if (!a || ev.b !== 1) return;
        fx.sparkles(a.x, a.y, a.z + 0.3, 10, C.positive, 1.2, 1.8);
        tag('sparkles');
        return;
      case EV.VEND:
        iconPop(ev.a, ev.b, view);
        if (a) fx.sparkles(a.x, a.y, a.z + 1.2, 8, C.gold, 0.9, 1.6);
        return;
      case EV.HEAD_BONK:
        if (!a) return;
        kick(ev.a, 'squash', 2.5);
        fx.impact(a.x, a.y, a.z + 1.1, 0, 0, 0.7, 6, settings().reducedFlashing);
        tag('impact');
        pop(Word.BONK, a.x, a.y, a.z + 2, 0.8, ev.a);
        return;
      default:
        // CROWN_LOST, HAZARD_WARN / HAZARD_FIRE, TILE, WEDGE, BUMPER, SPUN, COUNTDOWN: map-owned or UI.
        return;
    }
  }

  function emote(b: BeanView, kind: number): void {
    const s = particles.spec;
    const cell = EMOTE_CELL[kind] ?? Cell.SPARKLE;
    const n = Math.max(2, Math.round(4 * fx.density));
    for (let i = 0; i < n; i++) {
      s.reset();
      s.x = b.x + rand(-0.4, 0.4);
      s.y = b.z + 1.4 + rand(0, 0.3);
      s.z = b.y + rand(-0.2, 0.2);
      s.vy = rand(1.2, 2);
      s.vx = rand(-0.6, 0.6);
      s.drag = 1.5;
      s.life = rand(0.7, 1);
      s.size = 0.32;
      s.sizeEnd = 0.24;
      s.fadeAt = 0.6;
      s.spin = rand(-2, 2);
      s.cell = cell;
      s.color(identity(b));
      particles.emit(s);
    }
    tag('emote');
  }

  function entity(view: FrameView, id: number): EntityView | undefined {
    for (const e of view.ents) if (e.id === id) return e;
    return undefined;
  }

  function trailBurst(b: BeanView, n: number): void {
    const kind = b.cosmetics.trail;
    if (kind <= 0) return;
    const c = identity(b);
    for (let i = 0; i < n; i++) fx.trail(kind, b.x, b.y, b.z, c, time + i * 0.05);
    tag('trail');
  }

  // ---- State-driven effects -------------------------------------------------------------------

  function stateFx(view: FrameView, dt: number): void {
    const density = fx.density;
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const b = view.beans[slot];
      if (!b.active || b.ghost || b.out) {
        seen[slot] = 0;
        continue;
      }
      const moved = seen[slot] ? Math.hypot(b.x - lastX[slot], b.y - lastY[slot]) : 0;
      lastX[slot] = b.x;
      lastY[slot] = b.y;
      seen[slot] = 1;
      if (dt <= 0) continue;
      const speed = Math.hypot(b.vx, b.vy);
      // Trail cosmetic (§7.3) during lunge, flight and fall.
      if (b.cosmetics.trail > 0 && trailState(b, speed)) {
        trailAcc[slot] += dt * JUICE.trailRate * density;
        const c = identity(b);
        while (trailAcc[slot] >= 1) {
          trailAcc[slot] -= 1;
          fx.trail(b.cosmetics.trail, b.x, b.y, b.z, c, time);
        }
      } else {
        trailAcc[slot] = 0;
      }
      // Speed lines on fast flights (§11.1 tumble / throw).
      const fast = speed > JUICE.speedLineMin && (b.state === BS.TUMBLE || b.state === BS.FALLING || b.state === BS.YANKED);
      if (fast) {
        lineAcc[slot] += dt * JUICE.speedLineRate * Math.max(0.5, density) * Math.min(2, speed / 14);
        while (lineAcc[slot] >= 1) {
          lineAcc[slot] -= 1;
          fx.speedLine(b.x, b.y, b.z, b.vx, b.vy, b.vz);
        }
      } else {
        lineAcc[slot] = 0;
      }
      // Skid marks while slipping (§11.3, fade over 4 s).
      if (b.state === BS.SLIP && moved > 0) {
        skidAcc[slot] += moved;
        if (skidAcc[slot] >= JUICE.skidStep) {
          skidAcc[slot] = 0;
          fx.skid(b.x, b.y, groundUnder(b.x, b.y, b.z), Math.atan2(b.vy, b.vx));
        }
      }
      // Big Shake footsteps leave dust rings (§11.3).
      if (b.big && b.z < 0.1 && b.state === BS.NORMAL && moved > 0) {
        stepAcc[slot] += moved;
        if (stepAcc[slot] >= JUICE.bigStep) {
          stepAcc[slot] = 0;
          fx.dustRing(b.x, b.y, groundUnder(b.x, b.y, b.z), 0.7, 6);
        }
      }
    }
    // Crates: a dust ring when one lands (§11.3 "dust on land").
    for (const e of view.ents) {
      if (e.kind !== EK.CRATE) continue;
      const prev = crateZ.get(e.id);
      crateZ.set(e.id, e.z);
      if (prev !== undefined && prev > 0.12 && e.z <= 0.12) {
        const h = groundUnder(e.x, e.y);
        fx.dustRing(e.x, e.y, h, 1.1, 10);
        fx.dustPuffs(e.x, e.y, h, 5, 0, 0, 2, 0.45);
        log.add('crateLand', EV.CRATE_DROP);
      }
    }
    if (crateZ.size > 32) crateZ.clear();
  }

  /**
   * Ledge cooldown (`BeanView.ledgeCd`, "sweaty hands", WP1's cheap-death note): a comic sweat drop beside the head
   * that wells up and slides down on a loop, and drips falling from both hands. The ground ring is a
   * Telegraphs decal. Hidden while hanging or climbing (the stomp ring owns that) and in BONK CAM.
   */
  function gripFx(view: FrameView, dt: number, calm: boolean): void {
    for (let slot = 0; slot < MAX_BEANS; slot++) {
      const b = view.beans[slot];
      const left = b.ledgeCd;
      if (left <= 0 || !inPlay(b) || b.state === BS.LEDGE || b.state === BS.CLIMB || b.state === BS.FALLING) {
        dripAcc[slot] = 0;
        continue;
      }
      const fade = Math.min(1, left / 40);
      if (anchors.headOf(slot, headTmp)) {
        // A 1.1 s loop: the drop wells up (scale pop), slides 0.18 m down the temple, fades.
        const k = calm ? 0.5 : (time / 1.1 + slot * 0.37) % 1;
        const grow = Math.min(1, k / 0.18);
        const s = particles.spec.reset();
        s.x = headTmp.x + 0.46 * b.radius / TUNE.bean.radius;
        s.y = headTmp.y - 0.05 - 0.2 * k * k;
        s.z = headTmp.z + 0.05;
        s.size = 0.5 * (0.55 + 0.45 * grow);
        s.aspect = 1.15;
        s.rot = 0.35;
        s.cell = Cell.DROP;
        s.color(C.sky);
        s.alpha = fade * (k > 0.85 ? 1 - (k - 0.85) / 0.15 : 1);
        particles.sprite(s);
      }
      if (dt <= 0) continue;
      dripAcc[slot] += dt * 6 * Math.max(0.5, fx.density);
      while (dripAcc[slot] >= 1) {
        dripAcc[slot] -= 1;
        // Hands hang at the sides, a little forward (shoulders ±0.40 m, §12.4).
        const side = Math.random() < 0.5 ? -1 : 1;
        const px = -Math.sin(b.facing) * side;
        const py = Math.cos(b.facing) * side;
        const r = b.radius / TUNE.bean.radius;
        const s = particles.spec.reset();
        s.x = b.x + (px * 0.5 + Math.cos(b.facing) * 0.12) * r;
        s.z = b.y + (py * 0.5 + Math.sin(b.facing) * 0.12) * r;
        s.y = b.z + 0.42 * r;
        s.vx = px * rand(0.2, 0.6);
        s.vz = py * rand(0.2, 0.6);
        s.vy = rand(0.3, 1.2);
        s.gravity = 12;
        s.life = 0.42;
        s.size = 0.13;
        s.sizeEnd = 0.08;
        s.curve = Curve.POP;
        s.fadeAt = 0.7;
        s.cell = Cell.DROP;
        s.floor = groundUnder(b.x, b.y, b.z);
        s.bounce = -1;
        s.color(C.sky);
        particles.emit(s);
      }
    }
  }

  function stepSlow(dtReal: number): void {
    if (!slow) {
      timeScale = 1;
      return;
    }
    slow.leftSec -= dtReal;
    if (slow.leftSec <= 0) {
      slow = null;
      timeScale = 1;
      return;
    }
    const out = slow.leftSec < JUICE.slowOutSec ? 1 - slow.leftSec / JUICE.slowOutSec : 0;
    timeScale = slow.scale + (1 - slow.scale) * out * out;
  }

  function updateMoment(view: FrameView): void {
    // GAME! also starts from the grade (e.g. when the final KO arrived as this client joined).
    if (view.grade === 'game' && view.source !== 'replay' && gameShown !== view.roundId) {
      // A match that ends without a final KO (time cap, crown goal) frames its winner, or the arena
      // center on a draw.
      const w = winner !== NO_SLOT ? view.beans[winner] : undefined;
      const c = MAPS[view.mapId]?.center;
      startGame(view, w ? w.x : c?.x ?? 0, w ? w.y : c?.y ?? 0, NO_SLOT);
    }
    if (!moment) return;
    if (winner === NO_SLOT && view.source !== 'replay') winner = findWinner(view, moment.victim);
    if (moment.winner === NO_SLOT && winner !== NO_SLOT) moment.winner = winner;
    const stale = view.roundId !== momentRound || view.source === 'replay'
      || (moment.kind === 'finalKo' && !slow)
      || (moment.kind === 'game' && view.grade !== 'game');
    if (stale) moment = null;
  }

  const director: GameVfx = {
    root,
    pops,
    particles,
    decals,
    telegraphs,
    guide,
    log,
    get moment() {
      return moment;
    },
    get timeScale() {
      return timeScale;
    },
    get letterbox() {
      return letterbox;
    },
    get winner() {
      return winner;
    },
    onSim(ev, local, view) {
      log.current = ev.type;
      const v = view ?? last;
      pops.replay = v.source === 'replay';
      handle(ev, v, v.source === 'replay' && !local);
      log.current = 0;
    },
    present(view) {
      realDt = lastNow < 0 ? 0 : Math.min(0.1, Math.max(0, (view.now - lastNow) / 1000));
      lastNow = view.now;
      stepSlow(realDt);
      hitstop.present(view.beans, presented, realDt * 1000, view.source === 'replay' ? -1 : view.localSlot);
      shareFrame(pv, view, presented);
      if (timeScale !== 1 && view.source !== 'replay') {
        pv.dt = view.dt * timeScale;
        pv.timeScale = view.timeScale * timeScale;
      }
      last = pv;
      return pv;
    },
    update(view) {
      const dt = view.dt;
      time += dt;
      const s = settings();
      updateMoment(view);
      particles.begin(dt);
      decals.begin(dt);
      stateFx(view, dt);
      if (!view.replay || view.replay.kind !== 'bonkcam') gripFx(view, dt, s.reducedFlashing);
      telegraphs.update(view, dt, s.reducedFlashing);
      guide.update(view, dt, s.pushGuide && view.source !== 'replay' && view.roomPhase !== RoomPhase.INTRO);
      particles.end();
      decals.end();
      if (letterbox && !view.mode.matchPoint) letterbox = false;
    },
    beforeRender(camera, view) {
      pops.replay = view.source === 'replay';
      pops.update(view.dt, realDt, camera, anchors);
      if (!rigFx) return;
      const reduced = settings().reducedFlashing;
      for (let slot = 0; slot < MAX_BEANS; slot++) {
        const rig = deps.rigs[slot];
        if (rig && !reduced && hitstop.flashing(slot)) rigFx.flash(rig.root);
      }
      const rp = view.replay;
      if (rp && rp.kind === 'bonkcam' && rp.killerSlot !== NO_SLOT && rp.killerSlot < MAX_BEANS) {
        const k = view.beans[rp.killerSlot];
        const rig = deps.rigs[rp.killerSlot];
        if (k && k.active && rig) rigFx.outline(rig.root, identityHex(k.identity, settings().colorblind));
      }
    },
    afterRender() {
      rigFx?.restore();
    },
    hitstopMs(slot) {
      return hitstop.ms(slot);
    },
    setPalette(p) {
      // Dust must read against the floor it rises from: on pale floors (porcelain, frosting, enamel)
      // a warm grey a step darker than the floor; on dark ones (the pinball table) a pale cream.
      fx.dust.set(p.base);
      const lum = 0.2126 * fx.dust.r + 0.7152 * fx.dust.g + 0.0722 * fx.dust.b;
      fx.paleFloor = lum > 0.45;
      if (fx.paleFloor) fx.dust.lerp(DUST_SHADE, 0.32);
      else fx.dust.lerp(C.cream, 0.55);
    },
    setVoid(kind, p) {
      fx.electricVoid = kind === 'grid';
      fx.fogVoid = kind === 'lilac' || kind === 'foam';
      switch (kind) {
        case 'tea': fx.liquid.set('#8A4B22'); fx.foam.set('#F2D7B0'); break;
        case 'milk': fx.liquid.set('#F7F7FF'); fx.foam.set('#DDE8FF'); break;
        // White on white suds would vanish: every third puff takes the drum's aqua.
        case 'foam': fx.liquid.set('#6FD6D0'); fx.foam.set('#E8F8FF'); break;
        case 'grid': fx.liquid.set('#FF3EC8'); fx.foam.set('#2FF3FF'); break;
        default: fx.liquid.set(p.void); fx.foam.set('#FFFFFF'); break;
      }
    },
    setTier(t) {
      tier = t;
      particles.setBudget(TIERS[t].particles);
      particles.setBoost(t === 'high' || t === 'medium' ? ADDITIVE_BOOST : 1);
      fx.density = t === 'high' ? 1 : t === 'medium' ? 0.8 : t === 'low' ? 0.6 : 0.45;
    },
    setGround(fn) {
      ground = fn;
    },
    reset() {
      particles.clear();
      decals.clear();
      pops.clear();
      hitstop.reset();
      telegraphs.clear();
      crateZ.clear();
      trailAcc.fill(0);
      lineAcc.fill(0);
      seen.fill(0);
      honkAt.fill(-Infinity);
      slow = null;
      timeScale = 1;
      moment = null;
      winner = NO_SLOT;
      letterbox = false;
      log.clear();
    },
  };
  director.setTier(tier);
  return director;
}
